import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Checkbox, Input } from '@roger/ui-components';
import { Loader2, Pencil } from 'lucide-react';
import { differingKinnserValue, type PulledIdentity } from './kinnserPulled';
import type { RosterCounts } from './kinnserRoster';
import type { AgencyClinicIdentity } from '../../services/api/types.gen';

/**
 * Every clinic value an agency holds, fields down and clinics across.
 *
 * Every cell is editable and none is an input until asked. Sixteen fields
 * across nine clinics would be 144 permanent boxes, so a cell shows its value
 * and a pencil, and becomes an input when the pencil is pressed.
 *
 * What you type is written to the clinic's own row. It is not a correction
 * stored somewhere that outranks Kinnser: a sync fills a clinic's identity only
 * where the column is blank and never replaces a value that is already there,
 * so typing is durable on its own -- and clearing a cell is how you ask for
 * Kinnser's value back.
 *
 * Fields down and clinics across rather than one clinic at a time, because an
 * agency's clinics differ from each other in ways only a side-by-side shows --
 * Palmeira's three sit in three states with three different submitter IDs, and
 * reading that from three separate forms means holding them in your head.
 *
 * A cell shows what the clinic row holds, and names Kinnser's value under it
 * only where the two differ. A pull writes Kinnser's data to the agency's
 * tables in the same step, so they agree until somebody types here -- and from
 * then on nothing else on the screen could say which value the agency is not
 * using.
 */

type Group =
  'Clinic' | 'OASIS and CAHPS' | 'Billing contact' | 'Rosters' | 'Settings';

interface GridField {
  group: Group;
  label: string;
  /**
   * What somebody reading this would not otherwise know, or nothing.
   *
   * A note on every row was a column of small grey text saying where values
   * came from, which tells a reader nothing. What survives is the handful that
   * does: a field nobody can fetch, an OASIS number a value has to match, a
   * letter with a meaning, and the states where one field turns pre-claim
   * review on.
   */
  note?: string;
  /** The clinic column this reads. */
  stored: keyof AgencyClinicIdentity;
}

/** Nothing but the agency can supply it, so an empty one is a phone call. */
const ASK = 'Ask the agency for this';

const FIELDS: readonly GridField[] = [
  { group: 'Clinic', label: 'Name', stored: 'name' },
  { group: 'Clinic', label: 'NPI', stored: 'npi' },
  { group: 'Clinic', label: 'EIN', note: ASK, stored: 'ein' },
  { group: 'Clinic', label: 'Phone', stored: 'phone' },
  {
    group: 'Clinic',
    label: 'Contact email',
    note: 'General inbox, used for payer enrollment',
    stored: 'email',
  },
  { group: 'Clinic', label: 'Street address', stored: 'address' },
  { group: 'Clinic', label: 'City', stored: 'city' },
  {
    group: 'Clinic',
    label: 'State',
    note: 'Pre-claim review: IL, OH, TX, NC, FL, OK',
    stored: 'state',
  },
  { group: 'Clinic', label: 'ZIP', stored: 'zip_code' },
  {
    group: 'OASIS and CAHPS',
    label: 'CCN / PTAN',
    note: 'M0010',
    stored: 'hha_agency_id',
  },
  {
    group: 'OASIS and CAHPS',
    label: 'Branch ID',
    note: 'M0016. "N" means no branches',
    stored: 'cms_branch_id',
  },
  {
    group: 'OASIS and CAHPS',
    label: 'Submitter ID',
    note: 'HHA_AGENCY_ID, not the CCN',
    stored: 'cms_qies_agency_id',
  },
  {
    group: 'OASIS and CAHPS',
    label: 'SHP provider number',
    note: 'Empty unless the CAHPS vendor is SHP',
    stored: 'shp_provider_no',
  },
  {
    group: 'Billing contact',
    label: 'First name',
    stored: 'billing_contact_first_name',
  },
  {
    group: 'Billing contact',
    label: 'Last name',
    stored: 'billing_contact_last_name',
  },
  { group: 'Billing contact', label: 'Phone', stored: 'billing_contact_phone' },
];

/* What the group is, where the group's name does not say it. */
const GROUP_NOTES: Partial<Record<Group, string>> = {
  Settings: 'Sync does not modify settings',
};

const GROUPS: readonly Group[] = [
  'Clinic',
  'OASIS and CAHPS',
  'Billing contact',
  'Rosters',
  // Last, because these are Roger's switches rather than anything Kinnser
  // sent, and one of them changes what the next sync does.
  'Settings',
];

/* One height for every row, whether or not its label carries a note, because a
   column of values that steps up and down is hard to read across. */
const ROW = 'h-12 border-b border-border/60';

/* Pinned: the values scroll sideways past it, and a row whose label has gone
   off the left edge is a row of values about nothing.

   Its right-hand rule is drawn by the cell's own ::after, not by `border-r`.
   Under `border-collapse: collapse` the line between two cells belongs to the
   table's border grid rather than to either cell, so a border set here scrolls
   away with the values and leaves the pinned column with an open edge. The
   pseudo-element sits at `left-full`, exactly over the collapsed line, so at
   rest the two coincide and read as one rule -- and once the values scroll,
   this is the one that stays. */
const PINNED_RULE =
  'after:pointer-events-none after:absolute after:inset-y-0 after:left-full after:w-px after:bg-border';
const LABEL = `sticky left-0 z-10 w-64 bg-card px-3 text-left align-middle font-normal ${PINNED_RULE}`;

/* The vertical rules are here because row rules alone leave a wide row of
   values with nothing between them, and which clinic a value belongs to
   becomes a matter of tracking a column by eye across the screen. */
const CELL = 'border-l border-border/60 px-3 align-middle';

/* Not monospace. The figures are short and mostly not columns of digits --
   names, streets, a state code -- and a typewriter face makes every one of them
   look like machine output. Tabular numerals keep the digits that do line up. */
const VALUE = 'text-[13px] tabular-nums';

/* What a column needs to be readable. Nine clinics in a pane this wide left
   "1234567893" overlapping its neighbour, so the columns keep their width and
   the grid scrolls sideways instead. Wide enough for a street address and for
   more than two words of a clinic's name; the header carries the whole name on
   hover, because no width fits "Billet Home Health Incorporated". */
const LABEL_WIDTH = 16;
const CLINIC_WIDTH = 15;
const gridWidth = (clinics: number) =>
  `${LABEL_WIDTH + clinics * CLINIC_WIDTH}rem`;

/**
 * One horizontal scroll across both tables, kept in step.
 *
 * The strip of clinic names and the grid below it are separate scrollers,
 * because a single scroller around both would be the scrollport the strip
 * sticks to -- a box counts as a scroll container when either axis scrolls --
 * and the strip has to stick to the top of the pane. So they are told to
 * agree, which is what keeps each name over its own column.
 */
function useSyncedScroll() {
  const panes = useRef<Map<HTMLDivElement, () => void>>(new Map());
  const syncing = useRef(false);
  return useCallback((node: HTMLDivElement | null) => {
    // React calls a callback ref with null as the node goes away. Both
    // scrollers are replaced whenever the clinic list changes -- picking
    // another agency, or passing through loading and error -- while this
    // component stays mounted, so without this the map and its listeners grow
    // for as long as the screen is open.
    if (node === null) {
      for (const [pane, off] of panes.current) {
        if (!pane.isConnected) {
          off();
          panes.current.delete(pane);
        }
      }
      return;
    }
    if (panes.current.has(node)) return;
    const onScroll = () => {
      if (syncing.current) return;
      syncing.current = true;
      for (const other of panes.current.keys()) {
        if (other !== node) other.scrollLeft = node.scrollLeft;
      }
      // Released on the next frame: setting scrollLeft fires each of their own
      // scroll events, and answering those would be a loop.
      requestAnimationFrame(() => {
        syncing.current = false;
      });
    };
    node.addEventListener('scroll', onScroll);
    panes.current.set(node, () => node.removeEventListener('scroll', onScroll));
  }, []);
}

const Columns: React.FC<{ clinics: readonly AgencyClinicIdentity[] }> = ({
  clinics,
}) => (
  <colgroup>
    <col style={{ width: `${LABEL_WIDTH}rem` }} />
    {clinics.map(clinic => (
      <col key={clinic.id} />
    ))}
  </colgroup>
);

/** A stored value as a reader sees it: the text, or a dash for nothing. */
function text(value: unknown): string {
  if (value === null || value === undefined) return '—';
  const shown = String(value).trim();
  return shown === '' ? '—' : shown;
}

const Cell: React.FC<{
  field: GridField;
  clinic: AgencyClinicIdentity;
  /** What Kinnser last sent for this clinic, or nothing if it never has. */
  pulled?: PulledIdentity;
  /** What the last save of this cell said went wrong, or nothing. */
  error?: string;
  onCommit: (value: string) => void;
}> = ({ field, clinic, pulled, error, onCommit }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing) input.current?.focus();
  }, [editing]);

  const stored = clinic[field.stored];
  // Named only where it differs. A sync fills a blank column and never
  // replaces a set one, so once somebody has typed here the two can disagree
  // for good -- and the cell alone cannot say which value the agency is not
  // using.
  const kinnser = differingKinnserValue(pulled, field.stored, stored);
  // The dash is for a reader. Editing starts from the value, so an empty cell
  // opens empty rather than with a dash in it to delete.
  const shown = text(stored);
  const actual = stored === null || stored === undefined ? '' : String(stored);

  const commit = (typed: string) => {
    setEditing(false);
    // Clearing asks for Kinnser's value back. Saving the blank would leave the
    // cell empty until the next sync fills it, so write Kinnser's value now.
    const theirs = pulled?.[field.stored];
    const next =
      typed.trim() === '' && theirs !== null && theirs !== undefined
        ? String(theirs).trim()
        : typed.trim();
    // The pencil seeds the box with what the cell holds, so opening a cell and
    // clicking away submits the same value back. Writing it would be a no-op
    // round trip, and on this screen it would also stamp the row.
    if (next === actual.trim()) return;
    onCommit(next);
  };

  if (editing) {
    return (
      <td className={CELL}>
        <Input
          ref={input}
          inputSize='sm'
          aria-label={`${field.label} for ${clinic.name}`}
          value={draft}
          onChange={event => setDraft(event.target.value)}
          /* Saved as you leave, not a moment later. A delay would have been
             worth it only if it stopped a save per cell while tabbing across a
             row, and the unchanged check below already does that -- what the
             timer added was a window where leaving the cell and doing anything
             else lost the edit with no request and no mark on the cell. */
          onBlur={() => commit(draft)}
          onKeyDown={event => {
            if (event.key === 'Enter') {
              event.preventDefault();
              commit(draft);
            }
            if (event.key === 'Escape') {
              event.preventDefault();
              setEditing(false);
            }
          }}
        />
      </td>
    );
  }

  return (
    <td className={`${CELL} ${VALUE} group/cell`}>
      <div className='flex items-center gap-1'>
        {/* The exact stored value on hover. A clipped NPI or address defeats a
            screen whose whole job is showing what the agency holds. */}
        <span className='block flex-1 truncate' title={shown}>
          {shown}
        </span>
        <button
          type='button'
          aria-label={`Edit ${field.label} for ${clinic.name}`}
          className='shrink-0 rounded p-0.5 text-muted-foreground opacity-0 hover:bg-muted hover:opacity-100 focus-visible:opacity-100 group-hover/cell:opacity-60'
          onClick={() => {
            setDraft(actual);
            setEditing(true);
          }}
        >
          <Pencil className='h-3 w-3' />
        </button>
      </div>
      {/* The old value stays on screen beside the reason. A cell that cleared
          itself on a refusal would read as saved. */}
      {kinnser !== null && (
        <span
          className='block truncate text-[11px] text-muted-foreground'
          title={kinnser}
        >
          {`Kinnser: ${kinnser}`}
        </span>
      )}
      {/* `alert`, or a refusal that replaces nothing on screen is silent to a
          screen reader -- the input has already gone by the time it appears. */}
      {error && (
        <span role='alert' className='block text-[11px] text-destructive'>
          {error}
        </span>
      )}
    </td>
  );
};

/** Kinnser's answer for auto-MRN, when it is not Roger's. */
const KinnserAutoMrn: React.FC<{
  clinic: AgencyClinicIdentity;
  pulled?: PulledIdentity;
}> = ({ clinic, pulled }) => {
  const theirs = pulled?.auto_mrn;
  // Three readings, not two: Kinnser having no answer is not the same as it
  // saying off, and a clinic no pull has seen has nothing to compare against.
  if (pulled === undefined) return null;
  const said = theirs === true ? 'on' : theirs === false ? 'off' : 'not set';
  if ((theirs === true) === (clinic.auto_mrn === true)) return null;
  return (
    <span className='truncate text-[11px] text-muted-foreground'>
      {`Kinnser: ${said}`}
    </span>
  );
};

/** Why a switch did not save, where the switch is. */
const Switched: React.FC<{ error?: string }> = ({ error }) =>
  error ? (
    <span role='alert' className='block text-[11px] text-destructive'>
      {error}
    </span>
  ) : null;

/**
 * Payers, physicians and staff: how many, rather than which.
 *
 * A clinic can have nine hundred physicians, so the field-by-field treatment
 * the identity rows get is impossible here and would answer nothing anyway --
 * nobody reads a roster. How many the agency holds is the useful fact, and it
 * is what a fill just wrote.
 *
 * Payers span the clinic columns because Roger keeps one payer list for the
 * agency while Kinnser sends one per clinic. There is no per-clinic number, and
 * repeating the agency's total across nine columns would invent one.
 *
 * Each row says "count", because a bare "Payers" beside 190 reads as an
 * identifier as easily as a total.
 */
const RosterRows: React.FC<{
  clinics: readonly AgencyClinicIdentity[];
  roster: RosterCounts | null;
}> = ({ clinics, roster }) => {
  // Absent from the map means none landed, not unknown: the counts are
  // grouped rows, so a clinic with nothing simply has no row to group. The
  // dash is reserved for the read that did not arrive, which is the one thing
  // a zero must not be mistaken for.
  const perClinic = [
    {
      label: 'Physician count',
      held: (clinicKey: string) =>
        roster ? (roster.physicians[clinicKey] ?? 0) : undefined,
    },
    {
      label: 'Staff count',
      held: (clinicKey: string) =>
        roster ? (roster.staff[clinicKey] ?? 0) : undefined,
    },
  ];
  return (
    <>
      <tr className={ROW}>
        {/* One number across every clinic column: Roger keeps one payer list
            for the agency, and the span says so without a note. */}
        <RosterLabel label='Payer count' />
        <td colSpan={clinics.length} className={CELL}>
          <Count value={roster?.payers} note='across agency' />
        </td>
      </tr>
      {perClinic.map(row => (
        <tr key={row.label} className={ROW}>
          <RosterLabel label={row.label} />
          {clinics.map(clinic => (
            <td key={clinic.id} className={CELL}>
              <Count
                value={
                  clinic.clinic_key ? row.held(clinic.clinic_key) : undefined
                }
              />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
};

/** A count, or a dash when the counts did not load. */
const Count: React.FC<{ value: number | undefined; note?: string }> = ({
  value,
  note,
}) => (
  <span className={VALUE}>
    {value === undefined ? (
      <span className='text-muted-foreground'>—</span>
    ) : (
      value.toLocaleString()
    )}
    {value !== undefined && note && (
      <span className='ml-1.5 text-[11px] text-muted-foreground'>{note}</span>
    )}
  </span>
);

const RosterLabel: React.FC<{ label: string }> = ({ label }) => (
  <th scope='row' className={LABEL}>
    <span className='block text-[13px] font-medium'>{label}</span>
  </th>
);

/**
 * Roger's own two switches, which no sync writes.
 *
 * Here rather than in a panel of their own because they are per clinic, and
 * the grid is already the place a clinic is a column. Leaving one out is the
 * only thing on this screen that changes what a sync does.
 */
const SettingsRows: React.FC<{
  clinics: readonly AgencyClinicIdentity[];
  onLeftOut: (clinic: AgencyClinicIdentity, leftOut: boolean) => void;
  onAutoMrn: (clinic: AgencyClinicIdentity, autoMrn: boolean) => void;
  pulledFor: (clinic: AgencyClinicIdentity) => PulledIdentity | undefined;
  errorFor: (
    clinicId: string,
    field: keyof AgencyClinicIdentity
  ) => string | undefined;
}> = ({ clinics, onLeftOut, onAutoMrn, pulledFor, errorFor }) => (
  <>
    <tr className={ROW}>
      <th scope='row' className={LABEL}>
        <span className='block text-[13px] font-medium'>
          Leave out of every sync
        </span>
        <span className='block truncate text-[11px] text-muted-foreground'>
          Roger keeps the clinic data it has
        </span>
      </th>
      {clinics.map(clinic => (
        <td key={clinic.id} className={CELL}>
          <Checkbox
            checked={clinic.is_kinnser === false}
            aria-label={`Leave ${clinic.name} out of every sync`}
            onCheckedChange={checked => onLeftOut(clinic, checked === true)}
          />
          {/* Without this a failed save was a tick that undid itself, which
              reads as a misclick rather than as a refusal. */}
          <Switched error={errorFor(clinic.id, 'is_kinnser')} />
        </td>
      ))}
    </tr>
    <tr className={ROW}>
      <th scope='row' className={LABEL}>
        <span className='block text-[13px] font-medium'>
          Roger assigns MRNs
        </span>
        <span className='block truncate text-[11px] text-muted-foreground'>
          No sync ever changes this
        </span>
      </th>
      {clinics.map(clinic => (
        <td key={clinic.id} className={CELL}>
          {/* Beside the box, not under it. Under it the caption appeared and
              disappeared with the value, which moved the whole row and shifted
              every other clinic's box as you went along. */}
          <div className='flex items-center gap-2'>
            <Checkbox
              checked={clinic.auto_mrn === true}
              aria-label={`Roger assigns MRNs for ${clinic.name}`}
              onCheckedChange={checked => onAutoMrn(clinic, checked === true)}
            />
            {/* Kinnser reports its own answer and Roger keeps its own, so this
                is the one setting where the two can disagree on purpose. */}
            <KinnserAutoMrn clinic={clinic} pulled={pulledFor(clinic)} />
          </div>
          <Switched error={errorFor(clinic.id, 'auto_mrn')} />
        </td>
      ))}
    </tr>
  </>
);

export const KinnserIdentityGrid: React.FC<{
  /** The agency's clinic rows; null until they arrive, or when they failed. */
  clinics: readonly AgencyClinicIdentity[] | null;
  /** Which of those two nulls this is. */
  clinicsFailed: boolean;
  /** Ask for them again. A blip should not cost the operator the agency. */
  onRetry: () => void;
  /** Write one value onto one clinic's row. Blank clears the column. */
  onEdit: (
    clinic: AgencyClinicIdentity,
    field: keyof AgencyClinicIdentity,
    value: string
  ) => void;
  /** What Kinnser last sent for a clinic, for the line under a changed cell. */
  pulledFor: (clinic: AgencyClinicIdentity) => PulledIdentity | undefined;
  /** What the last save of a cell said went wrong, if anything. */
  errorFor: (
    clinicId: string,
    field: keyof AgencyClinicIdentity
  ) => string | undefined;
  /** Leave this clinic out of every sync, or put it back. */
  onLeftOut: (clinic: AgencyClinicIdentity, leftOut: boolean) => void;
  /** Whether Roger assigns MRNs for this clinic. */
  onAutoMrn: (clinic: AgencyClinicIdentity, autoMrn: boolean) => void;
  /** What the agency holds now, or null when this read did not carry it. */
  roster: RosterCounts | null;
}> = ({
  clinics,
  clinicsFailed,
  onRetry,
  onEdit,
  pulledFor,
  errorFor,
  onLeftOut,
  onAutoMrn,
  roster,
}) => {
  // Before the early returns: a hook cannot be called conditionally, and this
  // component returns a line of text on three of its paths.
  const syncScroll = useSyncedScroll();

  if (clinicsFailed) {
    return (
      <div className='flex flex-col items-start gap-2 sm:flex-row sm:items-center'>
        <p className='text-sm text-muted-foreground'>
          Could not load this agency&rsquo;s clinics.
        </p>
        <Button size='sm' variant='outline' onClick={onRetry}>
          Try again
        </Button>
      </div>
    );
  }
  if (clinics === null) {
    return (
      <p className='flex items-center gap-2 text-sm text-muted-foreground'>
        <Loader2 className='h-4 w-4 animate-spin' />
        Loading the agency&rsquo;s clinics&hellip;
      </p>
    );
  }
  if (clinics.length === 0) {
    return (
      <p className='text-sm text-muted-foreground'>
        This agency has no clinics yet.
      </p>
    );
  }

  return (
    /* One grid, in one box, whose header row happens to be a second table.

       It has to be: a box that scrolls in either axis is the scrollport a
       sticky header sticks to, and this header has to stick to the pane, so it
       cannot live inside the table that scrolls sideways. So there are two
       tables over the same columns, told to scroll together and drawn as one
       box -- the strip closes the top, the grid closes the bottom, and there is
       no gap between them to give the seam away. */
    <div>
      {/* What the pencils do, said once above the grid. Nothing else says it:
          there is no Save button to imply the edit is held somewhere, and a
          cell naming Kinnser's value cannot explain itself to somebody who has
          not changed one yet. */}
      <p className='mb-3 flex items-start gap-1.5 text-xs text-muted-foreground'>
        {/* The same pencil the cells carry, so the sentence is visibly about
            them. `mt-px` sits it on the first line's baseline rather than
            centred against a sentence that can wrap. */}
        <Pencil aria-hidden className='mt-px h-3 w-3 shrink-0' />
        <span>
          Editing a value saves it to Roger straight away. Clear a value to take
          Kinnser&rsquo;s again.
        </span>
      </p>

      {/* The clinic names once, above everything, stuck to the top of the pane.

          The offset is negative and matches the pane's own padding. Rows scroll
          through that padding -- overflow clips at the padding box, while
          children lay out inside the content box -- so a strip parked at
          `top-0` leaves a band of moving values above itself. */}
      <div className='sticky -top-4 z-20 overflow-hidden rounded-t-md border bg-muted'>
        <div ref={syncScroll} className='overflow-x-auto scrollbar-hide'>
          {/* Named by `aria-label` rather than a visually hidden <caption>.
              `sr-only` is `position: absolute`, and a caption in a static table
              has no positioned ancestor, so its containing block is the
              document itself. */}
          <table
            aria-label="The agency's clinics"
            className='w-full table-fixed border-collapse text-sm'
            style={{ minWidth: gridWidth(clinics.length) }}
          >
            <Columns clinics={clinics} />
            <thead>
              <tr>
                {/* Empty: the rows name themselves, and a column of field names
                    does not need telling you so. The label stays for a screen
                    reader, which has no row to read it from. */}
                <th
                  scope='col'
                  className={`sticky left-0 z-10 bg-muted px-3 py-2 ${PINNED_RULE}`}
                >
                  <span className='sr-only'>Field</span>
                </th>
                {clinics.map(clinic => (
                  <th
                    key={clinic.id}
                    scope='col'
                    className='border-l border-border/60 px-3 py-2 text-left align-bottom'
                  >
                    <span
                      title={clinic.name}
                      className='block truncate text-[13px] font-semibold'
                    >
                      {clinic.name}
                    </span>
                    {/* Only when it adds something. A clinic Kinnser has no
                        name for takes its id as its name, so both lines read
                        "16378" and the header looks like it is stuttering. */}
                    {clinic.clinic_key &&
                      clinic.name.trim().toLowerCase() !==
                        clinic.clinic_key.trim().toLowerCase() && (
                        <span className='block truncate text-[11px] font-normal tabular-nums text-muted-foreground'>
                          {clinic.clinic_key}
                        </span>
                      )}
                  </th>
                ))}
              </tr>
            </thead>
          </table>
        </div>
      </div>

      {/* One table, not a box per group. Three bordered boxes over a sticky
          strip read as three things that happened to be aligned, and every one
          of them had its own scrollbar to keep in step. The groups are bands
          inside the one grid, which is what they are. */}
      <section className='overflow-hidden rounded-b-md border border-t-0 bg-card'>
        <div ref={syncScroll} className='overflow-x-auto scrollbar-hide'>
          <table
            aria-label="The agency's clinic identity"
            className='w-full table-fixed border-collapse text-sm [&>tbody:last-child>tr:last-child]:border-b-0'
            style={{ minWidth: gridWidth(clinics.length) }}
          >
            <Columns clinics={clinics} />
            {GROUPS.map(group => (
              <tbody key={group}>
                <tr>
                  {/* Spans the grid, so the name is not a cell in the label
                      column. The inner block is pinned left instead, which
                      keeps the group's name on screen when the values are
                      scrolled sideways. */}
                  <th
                    colSpan={1 + clinics.length}
                    scope='colgroup'
                    className='border-b bg-muted/50 p-0 text-left'
                  >
                    {/* `w-fit`, or there is nothing for `sticky` to do: a block
                        as wide as the cell it sits in has no room to slide, so
                        the name scrolls away with the values. */}
                    <div className='sticky left-0 flex w-fit flex-wrap items-baseline gap-x-2 px-3 py-2'>
                      <h3 className='text-sm font-semibold'>{group}</h3>
                      {GROUP_NOTES[group] && (
                        <span className='text-xs font-normal text-muted-foreground'>
                          {GROUP_NOTES[group]}
                        </span>
                      )}
                    </div>
                  </th>
                </tr>
                {group === 'Rosters' && (
                  <RosterRows clinics={clinics} roster={roster} />
                )}
                {group === 'Settings' && (
                  <SettingsRows
                    clinics={clinics}
                    onLeftOut={onLeftOut}
                    onAutoMrn={onAutoMrn}
                    pulledFor={pulledFor}
                    errorFor={errorFor}
                  />
                )}
                {FIELDS.filter(field => field.group === group).map(field => (
                  <tr key={field.label} className={ROW}>
                    <th scope='row' className={LABEL}>
                      <span className='block text-[13px] font-medium'>
                        {field.label}
                      </span>
                      {field.note && (
                        <span className='block truncate text-[11px] text-muted-foreground'>
                          {field.note}
                        </span>
                      )}
                    </th>
                    {clinics.map(clinic => (
                      <Cell
                        key={clinic.id}
                        field={field}
                        clinic={clinic}
                        pulled={pulledFor(clinic)}
                        error={errorFor(clinic.id, field.stored)}
                        onCommit={value => onEdit(clinic, field.stored, value)}
                      />
                    ))}
                  </tr>
                ))}
              </tbody>
            ))}
          </table>
        </div>
      </section>
    </div>
  );
};
